import React from "react";
import { fireEvent, render, screen, waitFor } from "@testing-library/react";
import "@testing-library/jest-dom";

const mockResolveDocumentDownloadLink = jest.fn();

jest.mock("../services/api", () => ({
  useDownloadDocument: () => jest.fn(),
  useResolveDocumentDownloadLink: () => mockResolveDocumentDownloadLink,
}));

jest.mock("../../../providers/WorkspaceProvider", () => ({
  useWorkspaceContext: () => ({ workspace: { id: "workspace-1" } }),
}));

jest.mock("@metalmind-ab/mmagent-chat-ui", () => ({
  ChatMessage: () => null,
  JobProgressIndicator: () => null,
  useChatStore: (selector: (state: unknown) => unknown) =>
    selector({
      backgroundJobs: [],
      removeBackgroundJob: jest.fn(),
      messages: [],
    }),
}));

const {
  ImageOverlayWrapper,
  ToolResultImage,
} = require("./FullPageChatLayout");

const legacyHref =
  "https://app.metalmind.ai/api/workspace/ida/documents/download-link/workspace-1%2Fdoc-1?workspace_id=workspace-1";
const currentAppEndpoint =
  "/api/workspace/ida/documents/download-link/workspace-1%2Fdoc-1?workspace_id=workspace-1";

describe("ImageOverlayWrapper internal document links", () => {
  afterEach(() => {
    jest.clearAllMocks();
    jest.restoreAllMocks();
  });

  it("never opens the rendered legacy origin when current-app resolution fails", async () => {
    mockResolveDocumentDownloadLink.mockRejectedValue(
      new Error("resolution failed"),
    );
    const open = jest.spyOn(window, "open").mockImplementation(() => null);
    jest.spyOn(console, "error").mockImplementation(() => undefined);

    render(
      <ImageOverlayWrapper>
        <a href={legacyHref}>Download the PDF</a>
      </ImageOverlayWrapper>,
    );

    fireEvent.click(screen.getByRole("link", { name: "Download the PDF" }));

    await waitFor(() =>
      expect(mockResolveDocumentDownloadLink).toHaveBeenCalledWith(
        currentAppEndpoint,
      ),
    );
    expect(open).not.toHaveBeenCalled();
  });

  it("normalizes a rendered legacy image before resolving it", async () => {
    mockResolveDocumentDownloadLink.mockResolvedValue({
      download_url: "https://storage.example/image.png",
      filename: "resolved-image.png",
    });

    render(
      <ImageOverlayWrapper>
        <img src={legacyHref} alt="" />
      </ImageOverlayWrapper>,
    );

    await waitFor(() =>
      expect(mockResolveDocumentDownloadLink).toHaveBeenCalledWith(
        currentAppEndpoint,
      ),
    );
    expect(
      screen.getByRole("img", { name: "resolved-image.png" }),
    ).toHaveAttribute("src", "https://storage.example/image.png");
  });

  it("keeps tool-result image resolution and failed downloads on the current app", async () => {
    mockResolveDocumentDownloadLink
      .mockResolvedValueOnce({
        download_url: "https://storage.example/image.png",
        filename: "resolved-image.png",
      })
      .mockRejectedValueOnce(new Error("download resolution failed"));
    const open = jest.spyOn(window, "open").mockImplementation(() => null);

    render(<ToolResultImage src={legacyHref} alt="Rendered result" />);

    await waitFor(() =>
      expect(mockResolveDocumentDownloadLink).toHaveBeenCalledWith(
        currentAppEndpoint,
      ),
    );
    fireEvent.click(screen.getByRole("button", { name: "Download image" }));

    await waitFor(() =>
      expect(mockResolveDocumentDownloadLink).toHaveBeenLastCalledWith(
        currentAppEndpoint,
      ),
    );
    expect(await screen.findByText("Image expired")).toBeInTheDocument();
    expect(open).not.toHaveBeenCalled();
  });
});
